import { defineStore } from "pinia";
import {
  collection,
  query,
  where,
  Timestamp,
  doc,
  updateDoc,
  addDoc,
  arrayUnion,
  orderBy,
  limit,
  getCountFromServer,
  setDoc,
  writeBatch,
  getDoc,
  getDocs,
  runTransaction,
} from "firebase/firestore";
import { db, converter } from "@/firebase";
import { useFirestore } from "@vueuse/firebase";
import { useAdminStore } from "./admin";
import { computed, Ref, ref, watch } from "vue";
import { getEnvCollection } from "@/firebase/collections";
import { Job, JobSheet, LineItem, Nullable, LineItemFiles } from "@/models";
import dayjs from "dayjs";
import { useToast } from "vue-toastification";
import * as Sentry from "@sentry/vue";
import { sanitizeForFirestoreUpdate } from "@/helpers/firestore";
import debounce from 'lodash.debounce'
import { useOperatorAdminStore } from "./operatorRole";
import { useTypesenseStore, TypesenseJobSheetResult } from "./typesense";
import { getActiveUniqueLineItems, upsertLineItemById } from "@/features/Jobs/JobsDetail/lineItems";

const toast = useToast();

export const useJobStore = defineStore("jobs", () => {
  const adminStore = useAdminStore();
  const operatorAdminStore = useOperatorAdminStore();
  const typesenseStore = useTypesenseStore();
  const userEnv = computed(() => adminStore.user?.environment);

  const selectedJobSheet: Ref<Nullable<JobSheet>> = ref(null);
  const selectedJob: Ref<Nullable<Job>> = ref(null);
  const filteredStatus: Ref<Nullable<string>> = ref(null);

  const envId = computed(() => adminStore.envId ?? operatorAdminStore.envId);

  const searchText = ref('');
  const isTodayOnlyMode = ref(false);

  // Initialize Typesense clients when environment is set
  watch(
    userEnv,
    async (to) => {
      if (!!to) {
        await typesenseStore.initTypesenseClients();
      }
    },
    { immediate: true }
  );

  function setSelectedJobSheet(job: JobSheet | null) {
    selectedJobSheet.value = job;
    if (!job) return;
    selectedJob.value = jobs.value?.find((j) => j.id === job.jobId) ?? null;
  }
  function setSelectedJob(job: Job) {
    selectedJob.value = job;
  }

  const jobSheetsQueryCount = computed(() => {
    if (!!envId.value && !filteredStatus.value || filteredStatus.value === null) {
      return query(
        collection(db, getEnvCollection(envId.value!, 'JOB_SHEETS')),
        orderBy("jobDate", "desc")
      ).withConverter(converter<JobSheet>())
    }
    return !!envId.value && !!filteredStatus.value &&
      query(
        collection(db, getEnvCollection(envId.value!, 'JOB_SHEETS')),
        where('status', "==", filteredStatus.value),
        orderBy("jobDate", "desc")
      ).withConverter(converter<JobSheet>())
  });

  const loadQueryCount = async () => {
    if (jobSheetsQueryCount.value && envId.value) {
      try {
        const data = await getCountFromServer(jobSheetsQueryCount.value)
        jobSheetCount.value = data.data().count
      } catch (error) {
        Sentry.captureException(error);
        console.error("Failed to load job sheet count:", error);
        toast.error("Could not load job sheet count.");
      }
    }
  }

  const jobSheetCount = ref(0)
  const jobSheetsPage = ref(1)
  const jobSheetsPerPage = ref(300)

  const loadMore = () => {
    jobSheetsPerPage.value += 100
  }
  const clearToOnePage = () => {
    jobSheetsPerPage.value = 100
  }

  watch(
    userEnv,
    async (to) => {
      if (!!to && jobSheetCount.value === 0) {
        loadQueryCount();
      }
    },
  )

  watch(jobSheetsQueryCount, async (to) => {
    await loadQueryCount();
  }, { immediate: true });

  const jobSheetsQuery = computed(() => {
    let q = null

    if (filteredStatus.value === null) {
      q = query(
        collection(db, getEnvCollection(envId.value!, 'JOB_SHEETS')),
        orderBy("jobDate", "desc"),
        limit(jobSheetsPerPage.value)
      ).withConverter(converter<JobSheet>())
      if (searchText.value === '') {
        return q;
      }
    }

    q = !!filteredStatus.value &&
      query(
        collection(db, getEnvCollection(envId.value!, 'JOB_SHEETS')),
        where('status', "==", filteredStatus.value),
        orderBy("jobDate", "desc"),
        limit(jobSheetsPerPage.value)
      ).withConverter(converter<JobSheet>())

    if (jobSheetsPage.value > 1) {
      q = query(
        collection(db, getEnvCollection(envId.value!, 'JOB_SHEETS')),
        where('status', "==", filteredStatus.value),
        orderBy("jobDate", "desc"),
        limit(jobSheetsPerPage.value)
      ).withConverter(converter<JobSheet>())
    }
    return q;
  });

  let jobSheetsRaw = ref<JobSheet[] | undefined>([]);
  try {
    jobSheetsRaw = useFirestore<JobSheet>(jobSheetsQuery, []);
  } catch (error) {
    Sentry.captureException(error);
    console.error("Error attaching Firestore listener for job sheets:", error);
  }

  const searchingJobsheetsOnServer = ref(false)
  const searchJobSheetResults = ref<TypesenseJobSheetResult[]>([])

  watch(
    searchText,
    debounce(async () => {
      if (searchText.value === '') {
        searchJobSheetResults.value = []
        return;
      }

      try {
        searchingJobsheetsOnServer.value = true
      

        const today = new Date();
        const todayTimestamp = Math.floor(today.getTime() / 1000);
        const filterQuery = isTodayOnlyMode.value 
          ? `jobDate:>=${todayTimestamp}` 
          : undefined;

        const { results } = await typesenseStore.searchJobSheets({
          search: searchText.value.toLowerCase(),
          perPage: 250,
          filter: filterQuery,
        })
                
        if (!results) {
          searchJobSheetResults.value = []
          return
        }
        
        let filtered = results.filter((j) => !j.deleted)
        
        if (isTodayOnlyMode.value) {
          filtered = filtered.filter((j) => !["Completed", "Approved"].includes(j.status))
        }
        
        if (filteredStatus.value && filteredStatus.value !== null) {
          filtered = filtered.filter((j) => j.status === filteredStatus.value)
        }
        
        searchJobSheetResults.value = filtered

      } catch (error) {
        Sentry.captureException(error);
        console.error("Error searching job sheets:", error);
        toast.error("An error occurred during search.");
      } finally {
        searchingJobsheetsOnServer.value = false
      }
    }, 500)
  )

  watch(
    filteredStatus,
    () => {
      if (searchJobSheetResults.value.length > 0) {
        if (filteredStatus.value && filteredStatus.value !== null) {
          searchJobSheetResults.value = searchJobSheetResults.value.filter(
            (j) => j.status === filteredStatus.value
          )
        } else if (!filteredStatus.value || filteredStatus.value === null) {
          if (searchText.value !== '') {
            searchText.value = searchText.value;
          }
        }
      }
    }
  )

  const searchJobSheets = computed(() => {
    if (searchJobSheetResults.value.length === 0) return []
    return searchJobSheetResults.value.map(result => ({
      ...result,
      jobDate: Timestamp.fromMillis(result.jobDate * 1000),
      customer: { Name: result.customerName, id: result.customerId },
      operator: {
        id: result.operatorId,
        firstName: result.operatorName?.split(' ')[0] || '',
        lastName: result.operatorName?.split(' ').slice(1).join(' ') || '',
      },
      stops: Number((result as any).stops) || 0,
      lineItems: [], 
    } as any))
  })

  const jobSheets = computed(() =>
    jobSheetsRaw.value?.filter((j) => !j.deleted)
  );

  const daysAgo = 200;
  const startQueryDate = Timestamp.fromDate(
    dayjs().subtract(daysAgo, "days").toDate()
  );
  const jobsQuery = computed(() =>
    query(
      collection(db, getEnvCollection(envId.value!, 'JOBS')),
      orderBy("createdAt", "desc"),
    ).withConverter(converter<Job>())
  );
  const jobs = useFirestore<Job>(jobsQuery);

  const selectedJobSheetsQuery = computed(
    () =>
      !!selectedJob.value?.id &&
      query(
        collection(db, getEnvCollection(envId.value!, 'JOB_SHEETS')),
        where("jobId", "==", selectedJob.value?.id)
      ).withConverter(converter<JobSheet>())
  );
  const selectedJobSheets = useFirestore<JobSheet>(selectedJobSheetsQuery);

  const todaysJobSheetsQuery = computed(() => {
    const todayDate = Timestamp.fromDate(dayjs().hour(0).minute(0).toDate());
    return query(
      collection(db, getEnvCollection(envId.value!, 'JOB_SHEETS')),
      where("jobDate", ">=", todayDate)
    ).withConverter(converter<JobSheet>());
  });
  const todaysJobSheetsRaw = useFirestore<JobSheet>(todaysJobSheetsQuery);
  const todaysJobSheets = computed(() =>
    todaysJobSheetsRaw.value
      ?.filter((j) => !["Completed", "Approved"].includes(j.status))
      .filter((j) => !j.deleted)
  );


  const myJobSheets = computed(() =>
    todaysJobSheets.value?.filter((j) => j.operator.id === adminStore.user?.id)
  );


  const filteredJobSheets = computed(() => {
    return jobSheets.value;
  });


  async function migrateJobSheetLineItems(jobsheetId: string, lineItemsToMigrate: LineItem[]): Promise<LineItem[]> {
    if (!jobsheetId || !lineItemsToMigrate || lineItemsToMigrate.length === 0) {
      return [];
    }

    const batch = writeBatch(db);
    const jobSheetRef = doc(db, getEnvCollection(envId.value!, 'JOB_SHEETS'), jobsheetId);
    const lineItemSubCollectionRef = collection(jobSheetRef, 'lineItems');

    const migratedLineItems: LineItem[] = [];

    for (const item of lineItemsToMigrate) {
      let lineItemDocRef;
      const itemDataForSubcollection: LineItem = { ...item };

      if (!itemDataForSubcollection.id) {
        lineItemDocRef = doc(lineItemSubCollectionRef);
        itemDataForSubcollection.id = lineItemDocRef.id;
        itemDataForSubcollection.createdAt = item.createdAt || new Date();
      } else {
        lineItemDocRef = doc(lineItemSubCollectionRef, itemDataForSubcollection.id);
      }
      itemDataForSubcollection.deleted = item.deleted || false;

      batch.set(lineItemDocRef, itemDataForSubcollection);

      if (itemDataForSubcollection.files && itemDataForSubcollection.files.length > 0) {
        const filesWithIds: LineItemFiles[] = [];
        for (const file of itemDataForSubcollection.files) {
          let fileDocRef;
          const fileDataForSubcollection: LineItemFiles = { ...file };

          if (!fileDataForSubcollection.id) {
            fileDocRef = doc(collection(lineItemDocRef, 'files'));
            fileDataForSubcollection.id = fileDocRef.id;
            fileDataForSubcollection.createdAt = file.createdAt || new Date();
          } else {
            fileDocRef = doc(collection(lineItemDocRef, 'files'), fileDataForSubcollection.id);
          }
          fileDataForSubcollection.lineItemId = itemDataForSubcollection.id;
          fileDataForSubcollection.deleted = file.deleted || false;

          batch.set(fileDocRef, fileDataForSubcollection);
          filesWithIds.push(fileDataForSubcollection);
        }
        itemDataForSubcollection.files = filesWithIds;
      }
      migratedLineItems.push(itemDataForSubcollection);
    }


    batch.update(jobSheetRef, {
      lineItems: migratedLineItems,
      lineItemsMigrated: true,
      updatedAt: new Date(),
      searchDocketNumbers: migratedLineItems
        .filter(li => !li.deleted)
        .map(li => li.docketNumber?.trim()?.toLowerCase() ?? '') || []
    });

    try {
      await batch.commit();
      toast.success("JobSheet line items successfully migrated!");
      return migratedLineItems;
    } catch (error) {
      console.error(`Error migrating jobSheet ${jobsheetId} line items:`, error);
      Sentry.captureException(error);
      toast.error("Failed to migrate job sheet line items.");
      throw error;
    }
  }


  async function updateLineItem(jobsheetId: string, lineItem: LineItem): Promise<string> {
    if (!jobsheetId || !lineItem) {
      return "";
    }

    try {
      const sanitizedLineItem = sanitizeForFirestoreUpdate(lineItem) as LineItem;
      const lineItemCollectionRef = collection(
        db,
        getEnvCollection(envId.value!, 'JOB_SHEETS'),
        jobsheetId,
        'lineItems'
      );
      const lineItemDocRef = sanitizedLineItem.id
        ? doc(lineItemCollectionRef, sanitizedLineItem.id)
        : doc(lineItemCollectionRef);

      sanitizedLineItem.id = lineItemDocRef.id;
      sanitizedLineItem.createdAt = sanitizedLineItem.createdAt || new Date();
      sanitizedLineItem.deleted = sanitizedLineItem.deleted || false;
      lineItem.id = lineItemDocRef.id;

      await processLineItemBatchUpdate(jobsheetId, lineItemDocRef, sanitizedLineItem);

      return lineItemDocRef.id;

    } catch (error) {
      Sentry.captureException(error);
      toast.error("Failed to initialize line item.");
      throw error;
    }
  }

  async function processLineItemBatchUpdate(jobsheetId: string, lineItemDocRef: any, sanitizedLineItem: LineItem): Promise<void> {
    const jobSheetRef = doc(db, getEnvCollection(envId.value!, 'JOB_SHEETS'), jobsheetId);
    // const lineItemSubCollectionRef = collection(jobSheetRef, 'lineItems');
    const cleanLineItem = sanitizeForFirestoreUpdate(sanitizedLineItem) as LineItem;

    if (cleanLineItem.files) {
      cleanLineItem.files = cleanLineItem.files.map(file => {
        const newFile = { ...file };
        delete (newFile as any).temporaryURL;
        return newFile;
      });
    }

    try {
      await runTransaction(db, async (transaction) => {
        const currentJobSheetSnap = await transaction.get(jobSheetRef);
        if (!currentJobSheetSnap.exists()) {
          throw new Error(`Job sheet ${jobsheetId} does not exist.`);
        }

        const currentJobSheetData = currentJobSheetSnap.data() as JobSheet;
        const jobSheetLineItems = upsertLineItemById(
          currentJobSheetData.lineItems || [],
          cleanLineItem,
        );
        const isAllLineItemsVerified = jobSheetLineItems.length > 0 &&
          jobSheetLineItems.every((item) => item.verified);

        transaction.set(lineItemDocRef, cleanLineItem, { merge: true });
        transaction.update(jobSheetRef, {
          lineItems: jobSheetLineItems,
          searchDocketNumbers: jobSheetLineItems
            .map((item) => item.docketNumber?.trim()?.toLowerCase() ?? ''),
          updatedAt: new Date(),
          ...(isAllLineItemsVerified && currentJobSheetData.status === "Completed"
            ? { status: "Approved" }
            : {}),
        });
      });

      if (cleanLineItem.files?.length) {
        const filesBatch = writeBatch(db);
        for (const file of cleanLineItem.files) {
          const fileSubCollectionRef = collection(lineItemDocRef, 'files');
          const fileDocRef = file.id
            ? doc(fileSubCollectionRef, file.id)
            : doc(fileSubCollectionRef);
          const persistedFile = {
            ...file,
            id: fileDocRef.id,
            createdAt: file.createdAt || new Date(),
            deleted: file.deleted || false,
            lineItemId: cleanLineItem.id,
          };
          filesBatch.set(fileDocRef, persistedFile, { merge: true });
        }
        await filesBatch.commit();
      }

      toast.success("Line item and parent job sheet updated or added successfully.");
    } catch (error) {
      Sentry.captureException(error);
      console.error("Error committing updateLineItem batch:", error);
      toast.error("Failed to save line item changes in background.");
      throw error;
    }
  }

  async function updateJobSheet(jobsheet: Partial<JobSheet>) {
    if (!jobsheet.id) return;

    try {
      const jobsheetRef = doc(db, getEnvCollection(envId.value!, "JOB_SHEETS"), jobsheet.id);
      const jobsheetToUpdate: Partial<JobSheet> = { ...jobsheet };

      delete jobsheetToUpdate.lineItems;
      delete jobsheetToUpdate.lineItemsMigrated;

      if (jobsheetToUpdate.notes) {
        jobsheetToUpdate.notes = jobsheetToUpdate.notes.map((note: any) => ({
          text: note.text,
          createdAt: note.createdAt instanceof Timestamp
            ? note.createdAt
            : Timestamp.fromDate(new Date(note.createdAt)),
        }));
      }

      await updateDoc(jobsheetRef, {
        ...jobsheetToUpdate,
        searchCustomerName: (jobsheetToUpdate.customer?.Name?.trim() ?? "").toLowerCase(),
        searchJobNumber: (jobsheetToUpdate.jobNumber?.trim() ?? "").toLowerCase(),
        searchReferenceNumber: (jobsheetToUpdate.referenceNumber?.trim() ?? "").toLowerCase(),
        searchAddress: (jobsheetToUpdate.address?.trim() ?? "").toLowerCase(),
        searchInstructions: (jobsheetToUpdate.instructions?.trim() ?? "").toLowerCase(),
        updatedAt: new Date(),
      });

    } catch (error) {
      Sentry.captureException(error);
      console.error("Error updating job sheet:", error);
      toast.error("Failed to update job sheet.");
      throw error;
    }
  }

  async function createJobSheet(
    jobsheet: Partial<JobSheet>,
    isDuplicate: boolean = false
  ): Promise<{ success: boolean; id?: string }> {
    const batch = writeBatch(db);
    let newJobSheetId: string | undefined;

    try {
      let jobId = jobsheet.jobId;
      if (!jobId) {
        const sterilizedJobRecord = sanitizeForFirestoreUpdate({
          address: jobsheet.address ?? '',
          customer: jobsheet.customer ?? '',
          customerName: jobsheet.customer?.Name ?? '',
          jobNumber: jobsheet.jobNumber ?? '',
          referenceNumber: jobsheet.referenceNumber ?? '',
          jobSheets: [],
          createdAt: new Date(),
          searchCustomerName: (jobsheet.customer?.Name?.trim() ?? "").toLowerCase() ?? '',
          searchJobNumber: (jobsheet.jobNumber?.trim() ?? "").toLowerCase() ?? '',
          searchReferenceNumber: (jobsheet.referenceNumber?.trim() ?? "").toLowerCase(),
          searchAddress: (jobsheet.address?.trim() ?? "").toLowerCase() ?? '',
          searchInstructions: (jobsheet.instructions?.trim() ?? "").toLowerCase() ?? '',
        });
        const newJobRecordRef = doc(collection(db, getEnvCollection(envId.value!, 'JOBS')));
        jobId = newJobRecordRef.id;
        batch.set(newJobRecordRef, sterilizedJobRecord);
      }

      let jobDate = jobsheet.jobDate?.toDate() ?? new Date();
      if (dayjs(jobDate).isBefore(dayjs(), "day")) {
        jobDate = new Date();
      }

      const initialLineItems = isDuplicate ? [] : (jobsheet.lineItems || []);

      const sterilizedJobsheet = sanitizeForFirestoreUpdate({
        ...jobsheet,
        id: undefined,
        createdAt: new Date(),
        status: "New",
        isInvoiced: false,
        notes: [],
        lineItems: initialLineItems.map(item => ({
          ...item,
          id: item.id || doc(collection(db, '_')).id,
          createdAt: item.createdAt || new Date(),
          deleted: item.deleted || false,
          files: item.files ? item.files.map(file => ({
            ...file,
            id: file.id || doc(collection(db, '_')).id,
            createdAt: file.createdAt || new Date(),
            deleted: file.deleted || false,
          })) : [],
        })),
        lineItemsMigrated: false,
        operator: isDuplicate ? null : jobsheet.operator,
        machineType: isDuplicate ? null : jobsheet.machineType,
        jobDate,
        jobId: jobId,
        searchCustomerName: (jobsheet.customer?.Name.trim() ?? "").toLowerCase(),
        searchJobNumber: (jobsheet.jobNumber?.trim() ?? "").toLowerCase(),
        searchReferenceNumber: (jobsheet.referenceNumber?.trim() ?? "").toLowerCase(),
        searchAddress: (jobsheet.address?.trim() ?? "").toLowerCase(),
        searchInstructions: (jobsheet.instructions?.trim() ?? "").toLowerCase(),
        searchDocketNumbers: initialLineItems.map((l) => l.docketNumber.trim().toLowerCase()) || [],
      });

      const newJobsheetRef = doc(collection(db, getEnvCollection(envId.value!, 'JOB_SHEETS')));
      newJobSheetId = newJobsheetRef.id;
      batch.set(newJobsheetRef, sterilizedJobsheet);

      const jobDocRef = doc(db, getEnvCollection(envId.value!, 'JOBS'), jobId);
      batch.update(jobDocRef, {
        jobSheets: arrayUnion(newJobsheetRef.id),
      });

      await batch.commit();
      if (!isDuplicate) toast.success("New jobsheet created");
      return { success: true, id: newJobSheetId };
    } catch (error) {
      Sentry.captureException(error);
      toast.error(`An error occurred while creating jobsheet: ${error}`);
      return { success: false };
    }
  }

  async function deleteJobSheet(jobsheet: JobSheet) {
    try {
      const batch = writeBatch(db);
      const jobSheetDocRef = doc(db, getEnvCollection(envId.value!, 'JOB_SHEETS'), jobsheet.id!);

      batch.update(jobSheetDocRef, {
        deleted: true,
        updatedAt: new Date(),
        lineItems: (jobsheet.lineItems || []).map(li => ({ ...li, deleted: true })),
        searchDocketNumbers: [],
      });


      const lineItemsQuerySnap = await getDocs(collection(jobSheetDocRef, 'lineItems'));
      for (const lineItemDoc of lineItemsQuerySnap.docs) {
        batch.update(lineItemDoc.ref, { deleted: true });
        const filesQuerySnap = await getDocs(collection(lineItemDoc.ref, 'files'));
        for (const fileDoc of filesQuerySnap.docs) {
          batch.update(fileDoc.ref, { deleted: true });
        }
      }

      await batch.commit();
      toast.success("Deleted jobsheet");
    } catch (error) {
      Sentry.captureException(error);
      console.error("Error deleting job sheet:", error);
      toast.error("Failed to delete job sheet.");
      throw error;
    }
  }

  const clear = () => {
    selectedJobSheet.value = null;
    selectedJob.value = null;
    filteredStatus.value = null;
  }

  const loadLineItemsForJobSheetCount = async (jobsheetId: string): Promise<number> => {
    if (!jobsheetId) return 0;
    try {
      const jobSheetRef = doc(db, getEnvCollection(envId.value!, 'JOB_SHEETS'), jobsheetId);
      const lineItemsCollectionRef = collection(jobSheetRef, 'lineItems');
      const q = query(lineItemsCollectionRef, where('deleted', '==', false));
      const snapshot = await getCountFromServer(q);
      return snapshot.data().count;
    } catch (error) {
      Sentry.captureException(error);
      console.error("Error counting line items for job sheet:", error);
      return 0;
    }
  }

  const getLineItmesForJobSheet = async (jobsheetId: string): Promise<LineItem[]> => {
    if (!jobsheetId) return [];
    try {
      const jobSheetRef = doc(db, getEnvCollection(envId.value!, 'JOB_SHEETS'), jobsheetId);
      const lineItemsCollectionRef = collection(jobSheetRef, 'lineItems');
      const q = query(lineItemsCollectionRef, where('deleted', '==', false), orderBy('createdAt', 'asc'));
      const querySnapshot = await getDocs(q);
      const lineItems: LineItem[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data() as LineItem;
        lineItems.push(data);
      });
      return getActiveUniqueLineItems(lineItems);
    } catch (error) {
      Sentry.captureException(error);
      console.error("Error fetching line items for job sheet:", error);
      return [];
    }
  }

  /**
   * Check if a job sheet can be reassigned to a different customer
   * Returns validation result with warnings
   */
  async function canReassignJobSheet(jobSheetId: string): Promise<{
    canReassign: boolean;
    reason?: string;
    warnings?: string[];
  }> {
    try {
      if (!envId.value) {
        return { canReassign: false, reason: "Environment not loaded" };
      }

      const jobSheetRef = doc(db, getEnvCollection(envId.value, 'JOB_SHEETS'), jobSheetId);
      const jobSheetDoc = await getDoc(jobSheetRef);

      if (!jobSheetDoc.exists()) {
        return { canReassign: false, reason: "Job sheet not found" };
      }

      const jobSheet = { ...jobSheetDoc.data(), id: jobSheetDoc.id } as JobSheet;

      // BLOCKER: Already invoiced
      if (jobSheet.isInvoiced) {
        return {
          canReassign: false,
          reason: "Cannot reassign invoiced job sheets. Please void or delete the invoice first."
        };
      }

      const warnings: string[] = [];

      // Check if in draft invoice (completed but not invoiced)
      if (!jobSheet.isInvoiced && jobSheet.status === 'Completed') {
        warnings.push("This job sheet is in a draft invoice. It will be moved to a different invoice group after reassignment.");
      }

      // Check job-level consistency
      if (jobSheet.jobId) {
        const jobSheetsQuery = query(
          collection(db, getEnvCollection(envId.value, 'JOB_SHEETS')),
          where('jobId', '==', jobSheet.jobId)
        );
        const jobSheetsSnapshot = await getDocs(jobSheetsQuery);
        const otherJobSheets = jobSheetsSnapshot.docs
          .map(doc => ({ ...doc.data(), id: doc.id } as JobSheet))
          .filter(js => js.id !== jobSheetId);

        const hasMultipleCustomers = otherJobSheets.some(
          js => js.customer?.id && js.customer.id !== jobSheet.customer?.id
        );

        if (hasMultipleCustomers || otherJobSheets.length > 0) {
          warnings.push(`This job has ${otherJobSheets.length + 1} job sheet(s). Reassigning will create mixed customers within the same job.`);
        }
      }

      return { canReassign: true, warnings };
    } catch (error) {
      Sentry.captureException(error);
      console.error("Error checking job sheet reassignment:", error);
      return {
        canReassign: false,
        reason: "An error occurred while validating reassignment. Please try again."
      };
    }
  }

  /**
   * Reassign a job sheet to a different customer
   * Updates customer reference and search index
   */
  async function reassignJobSheetCustomer(
    jobSheetId: string,
    newCustomerId: string
  ): Promise<void> {
    try {
      if (!envId.value) {
        throw new Error("Environment not loaded");
      }

      // Validate reassignment
      const validation = await canReassignJobSheet(jobSheetId);
      if (!validation.canReassign) {
        throw new Error(validation.reason || "Cannot reassign this job sheet");
      }

      // Get new customer
      const newCustomerRef = doc(db, getEnvCollection(envId.value, 'COMPANIES'), newCustomerId);
      const newCustomerDoc = await getDoc(newCustomerRef);

      if (!newCustomerDoc.exists()) {
        throw new Error("Target customer not found");
      }

      const newCustomer = { ...newCustomerDoc.data(), id: newCustomerDoc.id } as any;

      // Get current job sheet for audit trail
      const jobSheetRef = doc(db, getEnvCollection(envId.value, 'JOB_SHEETS'), jobSheetId);
      const jobSheetDoc = await getDoc(jobSheetRef);
      const currentJobSheet = jobSheetDoc.data() as JobSheet;

      // Update job sheet with new customer
      await updateDoc(jobSheetRef, {
        customer: newCustomer,
        customerName: newCustomer.Name ?? '',
        searchCustomerName: (newCustomer.Name?.trim() ?? "").toLowerCase(),
        updatedAt: Timestamp.now(),
        // Audit trail
        previousCustomerId: currentJobSheet.customer?.id ?? null,
        reassignedAt: Timestamp.now(),
      });

      toast.success(`Job sheet reassigned to ${newCustomer.Name}`);
    } catch (error) {
      Sentry.captureException(error);
      console.error("Error reassigning job sheet:", error);
      toast.error(error instanceof Error ? error.message : "Failed to reassign job sheet");
      throw error;
    }
  }

  return {
    setSelectedJobSheet,
    setSelectedJob,
    updateJobSheet,
    updateLineItem,
    createJobSheet,
    deleteJobSheet,
    migrateJobSheetLineItems,
    canReassignJobSheet,
    reassignJobSheetCustomer,
    clear,
    loadMore,
    clearToOnePage,
    loadLineItemsForJobSheetCount,
    getLineItmesForJobSheet,
    searchingJobsheetsOnServer,
    searchJobSheets,
    searchText,
    isTodayOnlyMode,
    jobSheetsPage,
    jobSheetsPerPage,
    jobSheetCount,
    jobSheets,
    jobs,
    filteredStatus,
    filteredJobSheets,
    todaysJobSheets,
    myJobSheets,
    selectedJobSheet,
    selectedJob,
    selectedJobSheets,
  };
});
